import { Fragment } from 'react';

import { makeStyles } from '@material-ui/core';

import { Tag, TagGroup } from '@backstage/ui';

import {
  ActivityChangeOptions,
  ActivityItem,
} from '@spotify/plugin-confidence-activity-react';
import { createWorkflowUpdatesChangeExtension } from '@spotify/plugin-confidence-workflows-react';

const useStyles = makeStyles({
  itemContainer: {
    display: 'flex',
    gap: 'var(--bui-space-1)',
  },
  addedText: {
    color: 'var(--bui-fg-success)',
  },
  removedText: {
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'line-through',
  },
});

interface Treatment {
  name?: string;
  weight: number;
  variant?: {
    name: string;
  };
  displayName?: string;
  description?: string;
  imageUri?: string;
}

interface AbTestData {
  treatments?: Treatment[];
}

const TreatmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const classes = useStyles();

  const oldValue = change.oldValue as AbTestData | null;
  const newValue = change.newValue as AbTestData | null;

  const oldTreatments = oldValue?.treatments ?? [];
  const newTreatments = newValue?.treatments ?? [];

  const oldTreatmentNames = new Set(
    oldTreatments.map(t => t.displayName || t.name || ''),
  );
  const newTreatmentNames = new Set(
    newTreatments.map(t => t.displayName || t.name || ''),
  );

  const addedTreatments = newTreatments.filter(
    t => !oldTreatmentNames.has(t.displayName || t.name || ''),
  );
  const removedTreatments = oldTreatments.filter(
    t => !newTreatmentNames.has(t.displayName || t.name || ''),
  );

  const weightChanges = newTreatments
    .map(newT => {
      const oldT = oldTreatments.find(
        t => (t.displayName || t.name) === (newT.displayName || newT.name),
      );
      if (oldT && oldT.weight !== newT.weight) {
        return {
          name: newT.displayName || newT.name || '',
          oldWeight: oldT.weight,
          newWeight: newT.weight,
        };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <Fragment>
      {addedTreatments.length > 0 && (
        <ActivityItem.Section label="Added treatments">
          {addedTreatments.map((treatment, index) => (
            <div key={index}>
              <span className={classes.addedText}>
                <TagGroup aria-label="Treatment name">
                  <Tag size="small">
                    {treatment.displayName || treatment.name}
                  </Tag>
                </TagGroup>{' '}
                ({treatment.weight / 100}%)
              </span>
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {removedTreatments.length > 0 && (
        <ActivityItem.Section label="Removed treatments">
          {removedTreatments.map((treatment, index) => (
            <div key={index}>
              <span className={classes.removedText}>
                <TagGroup aria-label="Treatment name">
                  <Tag size="small">
                    {treatment.displayName || treatment.name}
                  </Tag>
                </TagGroup>{' '}
                ({treatment.weight / 100}%)
              </span>
            </div>
          ))}
        </ActivityItem.Section>
      )}

      {weightChanges.length > 0 && (
        <ActivityItem.Section label="Weight changes">
          {weightChanges.map((newChange, index) => (
            <div key={index} className={classes.itemContainer}>
              <TagGroup aria-label="Treatment name">
                <Tag size="small">{newChange!.name}</Tag>
              </TagGroup>
              :{' '}
              <span className={classes.removedText}>
                {newChange!.oldWeight / 100}%
              </span>{' '}
              <span className={classes.addedText}>
                {newChange!.newWeight / 100}%
              </span>
            </div>
          ))}
        </ActivityItem.Section>
      )}
    </Fragment>
  );
};

export const ABTEST_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'ab-test-change',
  predicate: ({ change }) => change.fieldPath === '/module_data/abtest',
  title: change => {
    const oldValue = change.oldValue as AbTestData | null;
    const newValue = change.newValue as AbTestData | null;

    const oldTreatments = oldValue?.treatments ?? [];
    const newTreatments = newValue?.treatments ?? [];

    if (newTreatments.length > oldTreatments.length) {
      const addedCount = newTreatments.length - oldTreatments.length;
      const oldTreatmentNames = new Set(
        oldTreatments.map(t => t.displayName || t.name || ''),
      );
      const addedTreatment = newTreatments.find(
        t => !oldTreatmentNames.has(t.displayName || t.name || ''),
      );
      if (addedTreatment && addedCount === 1) {
        return `Added treatment ${addedTreatment.displayName || addedTreatment.name}`;
      }
      return `Added ${addedCount} treatment${addedCount > 1 ? 's' : ''}`;
    }

    if (oldTreatments.length > newTreatments.length) {
      const removedCount = oldTreatments.length - newTreatments.length;
      const newTreatmentNames = new Set(
        newTreatments.map(t => t.displayName || t.name || ''),
      );
      const removedTreatment = oldTreatments.find(
        t => !newTreatmentNames.has(t.displayName || t.name || ''),
      );
      if (removedTreatment && removedCount === 1) {
        return `Removed treatment ${removedTreatment.displayName || removedTreatment.name}`;
      }
      return `Removed ${removedCount} treatment${removedCount > 1 ? 's' : ''}`;
    }

    const hasWeightChanges = newTreatments.some(newT => {
      const oldT = oldTreatments.find(
        t => (t.displayName || t.name) === (newT.displayName || newT.name),
      );
      return oldT && oldT.weight !== newT.weight;
    });

    if (hasWeightChanges) {
      return 'Updated treatment weights';
    }

    return 'Updated treatments';
  },
  render: TreatmentChangeRender,
});
